import { useEffect, useRef } from 'react';
import { useBuilderStore } from '../state/builderStore';
import {
  EMPTY_COUNTDOWN_PREVIEW,
  fetchCountdownPreview,
  type CountdownPageType,
  type CountdownPreviewPayload,
} from '../lib/countdownApi';

/**
 * THE COUNTDOWN APP, MIRRORED IN THE CANVAS (feature 0041 V6, 28/09/2026).
 *
 * « Pourquoi je dois la rajouter dans le builder, le builder doit la lire directement de l'appli » — and
 * she is right. A campaign is placed by its own targeting rules in the dashboard and injected by the page
 * reads; nothing about it lives in a page document. So the canvas asks the backend what the storefront
 * would serve for the page being edited, and paints exactly that: if there is a countdown, it shows; if
 * there is none, nothing shows.
 *
 * Mirrors `useResolvedMenus` in every respect that matters:
 *  - the FETCH KEY is what the campaigns are resolved against (page type + bound product), so typing in a
 *    text field, dragging a section or editing a colour re-fetches nothing;
 *  - the last answer is re-applied whenever the render context is REBUILT (a theme edit, a locale flip, a
 *    route change all mint a fresh context, which would drop the banner and the chips). `applyCountdownPreview`
 *    no-ops when nothing changed, so that self-healing loop settles after one pass;
 *  - best-effort: a 401/5xx/offline leaves the canvas with no countdown rather than an error. An editor
 *    that refuses to open because a decoration could not load is a worse editor.
 *
 * NOT polled. The campaigns change in the DASHBOARD, in another tab; a poll would spend a request a minute
 * on a page whose answer changes once a week. The canvas picks the change up on its next load — the same
 * contract as the COD config and the store's payment method, which are also read once.
 */

/** The editor's template → the page type the app's campaigns target. */
export function countdownPageTypeFor(template: string): CountdownPageType {
  if (template === 'product') return 'product';
  if (template === 'collection') return 'collection';
  // `home` covers the index AND the store's editorial/CMS routes — they are one surface to a seller, and
  // that is the backend's own rule (see `CountdownCampaigns`), so a `page` template resolves as home.
  return 'home';
}

export function useCountdownPreview(): void {
  const template = useBuilderStore((s) => s.route.template);
  const productId = useBuilderStore((s) => s.previewProduct?.id ?? '');
  const renderContext = useBuilderStore((s) => s.renderContext);
  const applyCountdownPreview = useBuilderStore((s) => s.applyCountdownPreview);

  const pageType = countdownPageTypeFor(template);
  const key = `${pageType}|${productId}`;
  /** The last answer for `key`, re-applied after every context rebuild. */
  const last = useRef<{ key: string; payload: CountdownPreviewPayload } | null>(null);

  useEffect(() => {
    let live = true;
    // A fresh key: clear what the previous page painted BEFORE the answer lands, or a product page's
    // banner would linger over the home template for as long as the request takes.
    if (last.current?.key !== key) {
      last.current = { key, payload: EMPTY_COUNTDOWN_PREVIEW };
      applyCountdownPreview(EMPTY_COUNTDOWN_PREVIEW);
    }
    void fetchCountdownPreview(pageType, productId || null).then((payload) => {
      if (!live) return;
      last.current = { key, payload };
      applyCountdownPreview(payload);
    });
    return () => {
      live = false;
    };
  }, [key, pageType, productId, applyCountdownPreview]);

  // Re-apply after a context rebuild — the banner and the chips ride the context and a rebuild drops them.
  useEffect(() => {
    const cached = last.current;
    if (!renderContext || !cached || cached.key !== key) return;
    applyCountdownPreview(cached.payload);
  }, [renderContext, key, applyCountdownPreview]);
}
